import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShareConfig } from '@/context/ShareConfigContext';

/**
 * Bidirectional bridge between the embedded marketplace and its parent domain host.
 *
 * Behavior:
 *   - When embedded inside an iframe on the configured parent origin, this component:
 *     (a) On mount, checks URL for `?embed_path=/xyz` or hash `#/xyz` and navigates the SPA to that path.
 *     (b) On every route change, posts `{ type: 'okv:navigate', path }` to the parent so it can
 *         update its own browser URL (via history.replaceState) to a shareable public URL like
 *         https://okgo.mx/tienda/ad/abc.
 *   - The parent domain must host a small HTML page (see /embed-host.html) that receives these
 *     messages and mirrors them into its own address bar. That way, users never leave the parent
 *     domain visually, but shared links always work.
 *
 * Non-invasive: does nothing when not embedded.
 */
export const PostMessageBridge = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isEmbedded, parent_origin, allowed_origins, embed_mode } = useShareConfig();

    // On mount: apply intended embed_path if present (?embed_path=/a/xyz or #/a/xyz)
    useEffect(() => {
        if (!isEmbedded) return;
        try {
            const url = new URL(window.location.href);
            let target = url.searchParams.get('embed_path');
            if (!target && url.hash && url.hash.length > 1) {
                target = url.hash.startsWith('#/') ? url.hash.slice(1) : `/${url.hash.slice(1)}`;
            }
            if (target && target !== location.pathname + location.search) {
                navigate(target, { replace: true });
            }
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmbedded]);

    // On every route change: notify parent
    useEffect(() => {
        if (!isEmbedded) return;
        try {
            const path = location.pathname + location.search;
            const targetOrigin = parent_origin || '*';
            window.parent.postMessage({
                type: 'okv:navigate',
                path,
                embed_mode: embed_mode || 'iframe',
            }, targetOrigin);
        } catch (_) {}
    }, [location.pathname, location.search, isEmbedded, parent_origin, embed_mode]);

    // Listen for parent → child navigate commands (e.g. deep-link changes after mount)
    useEffect(() => {
        if (!isEmbedded) return;
        const handler = (evt) => {
            try {
                if (parent_origin && evt.origin !== parent_origin && !(allowed_origins || []).includes(evt.origin)) return;
                const msg = evt.data;
                if (!msg || typeof msg !== 'object') return;
                if (msg.type === 'okv:navigate' && typeof msg.path === 'string') {
                    if (msg.path !== location.pathname + location.search) navigate(msg.path);
                }
            } catch (_) {}
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [isEmbedded, parent_origin, allowed_origins, navigate, location.pathname, location.search]);

    return null;
};
